/**
 * Data layer for Settings → API keys.
 *
 * Reads key metadata straight from `api_keys` (RLS: members of the active
 * business; the hash column has no client grant, so columns are listed
 * explicitly). Keys are created and revoked through the `create_api_key` and
 * `revoke_api_key` RPCs, which require an owner or admin of the business. The
 * plaintext key is returned once by `create_api_key` and is never stored.
 */

import { supabase } from '../../lib/supabase'

export type ApiScope = 'read' | 'write' | 'leads:write'

export interface ApiKeyRow {
  id: string
  name: string
  prefix: string
  scopes: ApiScope[]
  created_at: string
  last_used_at: string | null
  revoked_at: string | null
}

export interface CreatedKey {
  id: string
  name: string
  prefix: string
  scopes: ApiScope[]
  created_at: string
  /** Shown once. */
  key: string
}

export const SCOPE_OPTIONS: Array<{ value: ApiScope; label: string; hint: string }> = [
  {
    value: 'read',
    label: 'Read',
    hint: 'Programs, queue, sends, leads, stats, suppressions, views',
  },
  { value: 'write', label: 'Write', hint: 'Claims, sends, events, contacts, suppressions' },
  { value: 'leads:write', label: 'Leads only', hint: 'POST /v1/leads, for a website form' },
]

const COLUMNS = 'id, name, prefix, scopes, created_at, last_used_at, revoked_at'

export async function listApiKeys(): Promise<ApiKeyRow[]> {
  const { data, error } = await supabase
    .from('api_keys')
    .select(COLUMNS)
    .order('created_at', { ascending: false })
  if (error) throw new Error(error.message)
  return (data ?? []) as ApiKeyRow[]
}

export async function createApiKey(name: string, scopes: ApiScope[]): Promise<CreatedKey> {
  const { data, error } = await supabase.rpc('create_api_key', { p_name: name, p_scopes: scopes })
  if (error) throw new Error(friendlyError(error.message))
  return data as CreatedKey
}

export async function revokeApiKey(id: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('revoke_api_key', { p_id: id })
  if (error) throw new Error(friendlyError(error.message))
  return data === true
}

export function friendlyError(message: string): string {
  if (/already exists/i.test(message))
    return 'An active key with that name already exists. Pick another name.'
  if (/owner or admin/i.test(message))
    return 'Only an owner or admin of this business can manage API keys.'
  if (/at most 25/i.test(message))
    return 'This business already has 25 active keys. Revoke one first.'
  return message
}

export function scopeLabel(scopes: ApiScope[]): string {
  return scopes.map((s) => SCOPE_OPTIONS.find((o) => o.value === s)?.label ?? s).join(', ')
}

/** "3 minutes ago", "never". */
export function relativeTime(iso: string | null, now: number = Date.now()): string {
  if (!iso) return 'never'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return 'never'
  const s = Math.max(0, Math.round((now - t) / 1000))
  if (s < 60) return 'just now'
  const m = Math.round(s / 60)
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`
  const h = Math.round(m / 60)
  if (h < 48) return `${h} hour${h === 1 ? '' : 's'} ago`
  const d = Math.round(h / 24)
  return `${d} days ago`
}
